import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone'
import PropTypes from 'prop-types';
import DatePicker from "react-datepicker";
import { parseISO } from 'date-fns';
import { camelCase, uniq } from 'lodash';

export const FieldLabel = ({ label, name, note, required, type }) => {
    const isRadioOrCheckbox = type.toLowerCase().includes('checkbox') || type.toLowerCase().includes('radio'); // Check if "radio" or "checkbox" is in type for printing legend instead of label
    const labelText = required ? (<>{label}<abbr className="sq-form-required-field" title="required">*</abbr></>) : label;

    return (
        <>
            {
                isRadioOrCheckbox ? 
                <legend className="sq-form-question-title">
                    {labelText} 
                </legend>
                :
                <label className="sq-form-question-title" htmlFor={name}>
                    {labelText} 
                </label>
            }
            {note ? <FieldNote note={note} />: null}
        </>
    )
}

export const FieldNote = ({note}) => {
    return <em className="sq-form-question-note">{note}</em>
}

export const TextField = ({ name, maxlength, required, value, placeholder, onChangeEvent, error }) => {
    const onChange = (e) => {
        onChangeEvent(name, e.target.value);
    }

    return (
        <>
            <input 
                type="text" 
                name={name}
                defaultValue={value} 
                maxLength={maxlength}
                required={required}
                id={name} 
                placeholder={placeholder}
                className="sq-form-field" 
                onChange={onChange}
            />
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

export const TextAreaField = ({ name, maxlength, required, value, placeholder, onChangeEvent, error }) => {

    const onChange = (e) => {
        onChangeEvent(name, e.target.value);
    }
    
    return (
        <>
            <textarea 
                name={name}
                id="lessonsLearned" 
                rows="5" 
                maxLength={maxlength}
                className="sq-form-field valid"
                required={required}
                onChange={onChange}
                defaultValue={value}
                placeholder={placeholder}
            />
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

export const DateField = ({ name, value, onChangeEvent, error}) => {
    const formatDate = value ? parseISO(value) : undefined;

    const onSelectEvent = (date) => {
        const newDate = date ? date.toISOString() : '';
        onChangeEvent(name, newDate);
    }
    
    return (
        <>
            <DatePicker 
                className="sq-form-field"
                selected={formatDate} 
                onChange={onSelectEvent} 
                name={name}
                id={name}
                required
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
            />
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

export const CheckBoxFields = ({ name, options, selectedOptions, onChangeEvent, error }) => {

    const onChange = (e) => {
        const { checked, value } = e.target;
        const selection = selectedOptions === null ? [] : selectedOptions;
        const newSelections = checked ? [...selection, value] : selection.filter((item) => item !== value);
        onChangeEvent(name, newSelections);
    }
    const optionsFields = options.map((option, i) => {
        const inputId = camelCase(option);
        const isSelected = selectedOptions && selectedOptions.includes(option) ? 'checked' : null;

        return (
            <li key={i}>
                <input 
                    type="checkbox" 
                    name={name} 
                    id={inputId} 
                    value={option} 
                    defaultChecked={isSelected} 
                    onChange={onChange}
                />
                <label htmlFor={inputId}>{option}</label>
            </li>
        )
    })

    return (
        <>
            <ul className="sq-form-question-option-list">
                {optionsFields}
            </ul>
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
};

export const RadioBoxFields = ({ name, options, selectedOptions, onChangeEvent, error }) => {

    const onChange = (e) => {
        const { checked, value } = e.target;
        const newSelections = checked ? [value] : null;
        onChangeEvent(name, newSelections);
    }

    const optionsFields = options.map((option, i) => {
        const inputId = camelCase(option);
        
        const isSelected = selectedOptions && selectedOptions[0] === option ? 'checked' : null;

        return (
            <li key={i}>
                <input 
                    type="radio" 
                    name={name} 
                    id={inputId} 
                    value={option} 
                    defaultChecked={isSelected} 
                    onChange={onChange}
                />
                <label htmlFor={inputId}>{option}</label>
            </li>
        )
    })

    return (
        <>
            <ul className="sq-form-question-option-list">
                {optionsFields}
            </ul>
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

export const ItemAdder = ({ name, required, maxlength, maxnumber, suggestions = [], display, value, placeholder, onChangeEvent, changeInstantly, error }) => {
    const [items, setItems] = useState(value || []);
    const [changeInstant, setChangeInstant] = useState(false);
    // const [suggestedOptions, setSuggestedOptions] = useState(suggestions || []);
    const onMount = useRef(true);
    const ref = useRef();

    useEffect(() => {
        if (onMount.current) {
            onMount.current = false;
        } 
        else {
            if(changeInstant) {
                changeInstantly(name, items);
            }
            else {
                onChangeEvent(name, items);
            }
            
        }
    },[items]);

    const handleAddItem = (e) => {
        if(e.key === 'Enter' || e.type === 'click') {
            e.preventDefault();
            if(ref.current.value) {
                addItemToList(ref.current.value)
                ref.current.value = '';
            }
        }
    }

    const addItemToList = (newItem) => {
        if(!items.includes(newItem) && items.length < maxnumber) {
            const newItemList = [...items, newItem];
            setChangeInstant(false);
            setItems(newItemList);
        }
    }

    const updateItemInList = (e, index) => {
        const newItemList = [...items];
        newItemList.splice(index, 1, e.target.innerText);
        setChangeInstant(true);
        setItems(uniq(newItemList));
    }

    const removeItemFromList = (itemKey) => {
        const newItemList = [...items];
        newItemList.splice(itemKey, 1);
        setChangeInstant(false);
        setItems(newItemList);
    }

    const handleEdit = (e) => {
        if(e.target.innerText.length >= maxlength) {
            e.preventDefault();
        }
        if(e.type === 'paste') {
            const clipboardData = (e.clipboardData || window.clipboardData).getData('text');
            const allowedPasteLength = maxlength - e.target.innerText.length;
            if(clipboardData > allowedPasteLength) e.preventDefault();
        }
    }

    const PrintSuggestedOptions = suggestions.filter((item) => !items.includes(item)).map((option, oi) => {
        return (
            <li
                className={display + "-list__adder-input-suggestions-container"}
                key={oi}>
                    <span 
                        className={display + "-list__adder-input-suggestion"}
                        tabIndex="0"
                        onClick={() => addItemToList(option)}
                        onKeyDown={(e) => e.key === 'Enter' && addItemToList(option)}
                        >
                        {option}
                    </span>
            </li>
        )
    })

    const PrintItems = items.map((item, ii) => {
        return (
            <li className={display + "-list__item"} key={ii}>
                <span
                    tabIndex="0"
                    className={display + "-list__item-input"}
                    contentEditable="true"
                    suppressContentEditableWarning={true}
                    onBlur={(e) => updateItemInList(e, ii)}
                    onKeyPress={(e) => handleEdit(e)}
                    onPaste={(e) => handleEdit(e)}
                >
                    {item}
                </span>
                <button
                    className={display + "-list__item-remove"}
                    type="button"
                    onClick={() =>  removeItemFromList(ii)}
                    >
                    <i className="fas fa-times"></i>
                </button>
            </li>
        )
    });

    return (
        <>
            {PrintItems.length > 0 ? (
                <div className={display + "-list__container"}>
                    <ul className={display + "-list__list"}>
                        {PrintItems}
                    </ul>
                </div>
            ) : null}
            <div className={display + "-list__adder item-list__adder"}>
                <div className={display + "-list__adder-input-container"}>
                    <input 
                        ref={ref}
                        className={display + "-list__adder-input sq-form-field"}
                        type="text" 
                        name={name}
                        defaultValue='' 
                        required={required}
                        maxLength={maxlength}
                        id={name} 
                        placeholder={items.length >= maxnumber ? 'Limit reached!' : placeholder}
                        onKeyPress={handleAddItem}
                        disabled={items.length >= maxnumber ? true : false}
                        autoComplete="off"
                    />
                    
                    {PrintSuggestedOptions.length > 0 && items.length < maxnumber ? (
                        <div className={display + "-list__adder-input-suggestions"}>
                            <p className={display + "-list__suggestions-helper"}>Suggestions or write your own! </p>
                            <ul className={display + "-list__adder-input-suggestions-list"}>
                                {PrintSuggestedOptions}
                            </ul>
                        </div>
                        
                    ):
                    null}
                </div>
                
                <button
                    className={display + "-list__adder-submit"}
                    type="button"
                    onClick={handleAddItem}
                    onKeyPress={handleAddItem}
                >
                    Add more
                </button>

            </div>
            
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}


export const FileDropper = ({ name, required, value, selectedOptions, onChangeEvent, error }) => {
    const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone();
    const onMount = useRef(true);
    // console.log(selectedOptions);
    useEffect(() => {
        if (onMount.current) {
            onMount.current = false;
        } 
        else {
            if(acceptedFiles) {
                const fileOb = {
                    type: 'add',
                    fileContent: acceptedFiles,
                    name
                }

                if(required){
                    // If field is required, fill with temporary value till real file upload
                    onChangeEvent(name, ["temporary"]);
                }
                onChangeEvent('file', fileOb);
            }
        }
    },[acceptedFiles]);

    const files = acceptedFiles.map(file => (
        <p className="dropzone__selection" key={file.path}>
            <span className="dropzone__selection-label">Selected file: </span> {file.name}
        </p>
    ));

    const highlightBox = isDragActive ? ' drag-style' : '';

    return (
        <>
            {value ? (
                <p className="dropzone__selection-container">Attached document: <a 
                    className="dropzone__selection-file"
                    href={value[2]}
                    >
                        {value[1]}
                        </a>
                </p>
                )
                :
                (
                    <>
                        <div {...getRootProps()} className={"dropzone" + highlightBox}>
                            <input {...getInputProps()} />
                            {
                                isDragActive ?
                                    <p className="dropzone__desc">Drop the file here!</p> :
                                    <p className="dropzone__desc">Drag &apos;n&apos; drop a file here, or click to select a file.</p>
                            }
                        </div>
                        {files}
                    </>
                )
            }
            
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

export const ErrorMessage = ({additionalText, name}) => {
    return <span id={name} className="form__error" htmlFor={name}>This field is required. {additionalText}</span>
}

export const ToolTip = ({message}) => {
    return (
        <span className="tooltip__container">
            <i className="fas fa-info"></i>
            <span className="tooltip__msg">
                {message}
            </span>
        </span>
    )
}

// PropTypes
FieldLabel.propTypes = { 
    label: PropTypes.string, 
    name: PropTypes.string, 
    note: PropTypes.string, 
    required: PropTypes.bool, 
    type: PropTypes.string.isRequired 
}

FieldNote.propTypes = {
    note: PropTypes.string
}

TextField.propTypes = { 
    name: PropTypes.string, 
    maxlength: PropTypes.number, 
    required: PropTypes.bool, 
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

TextAreaField.propTypes = {
    name: PropTypes.string, 
    maxlength: PropTypes.number, 
    required: PropTypes.bool, 
    value: PropTypes.string, 
    placeholder: PropTypes.string,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

DateField.propTypes = { 
    name: PropTypes.string, 
    value: PropTypes.string, 
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

CheckBoxFields.propTypes = { 
    name: PropTypes.string, 
    options: PropTypes.array, 
    selectedOptions: PropTypes.array,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

RadioBoxFields.propTypes = { 
    name: PropTypes.string, 
    options: PropTypes.array, 
    selectedOptions: PropTypes.array,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

ItemAdder.propTypes = {
    name: PropTypes.string,
    required: PropTypes.bool,
    maxlength: PropTypes.number, 
    maxnumber: PropTypes.number, 
    display: PropTypes.string,
    suggestions: PropTypes.array,
    value: PropTypes.array,
    placeholder: PropTypes.string,
    onChangeEvent: PropTypes.func, 
    changeInstantly: PropTypes.func, 
    error: PropTypes.bool
}

FileDropper.propTypes = { 
    name: PropTypes.string, 
    required: PropTypes.bool, 
    value: PropTypes.array,
    selectedOptions: PropTypes.object,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

ErrorMessage.propTypes = {
    additionalText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]), 
    name: PropTypes.string
}

ToolTip.propTypes = {
    message: PropTypes.string
}
